/**
 * Every identity detail the legal pages depend on, in one place.
 *
 * When the domain changes (Netlify subdomain today, a purchased domain later),
 * change SITE_URL here and every legal page, contact address and canonical link
 * follows. Nothing else in the app hardcodes the domain.
 *
 * STATUS is deliberate: while it is 'draft', every legal page renders a visible
 * review banner. These documents are written to describe accurately what this
 * codebase actually does, but they have not been reviewed by a lawyer — flip
 * STATUS to 'published' only once someone qualified has read them and the
 * placeholders below are real.
 */
export const LEGAL = {
  STATUS: 'draft' as 'draft' | 'published',

  /** Product name as it appears in the documents. */
  SITE_NAME: 'Whiskey Sensei',

  /**
   * Public origin, no trailing slash. Registered 2026-08-27 at Cloudflare and
   * the canonical address from here on, whatever is serving it underneath
   * (Netlify today, CloudFront historically). This is the only place in the
   * app the domain appears.
   */
  SITE_URL: 'https://whiskeysensei.com',

  /**
   * The legal person responsible for the data — the "data controller" under
   * UK/EU GDPR. Confirmed 2026-08-27: operated by an individual, not a
   * company, trading as Turtle Hut Software — the same identity already
   * published on the owner's Table Tennis Scoreboard app. Naming both the
   * brand and the person keeps the controller identifiable, which GDPR
   * requires, while matching the owner's existing products. If an LLC is ever
   * formed this becomes the company name and number; see memory.md.
   */
  ENTITY_NAME: 'Turtle Hut Software, operated by Kuoshih Yang',

  /** Confirmed 2026-08-27: the owner is based in New York, USA. */
  GOVERNING_LAW: 'the State of New York, United States',

  /**
   * A domain role address, forwarded by Cloudflare Email Routing to the owner's
   * inbox. It is deliberately not a personal mailbox: a privacy contact sits on
   * a public page that scrapers harvest permanently.
   *
   * This previously held a Gmail address chosen for the same reason, which
   * turned out to be the Cloudflare account login — so the published page was
   * handing out half of a credential pair for the account that controls this
   * domain, its DNS and its secrets. A role address has no login attached to it
   * and can be repointed later without touching any account.
   */
  CONTACT_EMAIL: 'legal@whiskeysensei.com',

  /** Bumped by hand whenever the substance of a document changes. */
  LAST_UPDATED: '2026-08-27',

  /** Where the infrastructure physically runs. Drives the data-transfer sections. */
  HOSTING_REGION: 'AWS us-east-1 (Northern Virginia, United States)',
} as const;

/** True while any placeholder is still unfilled — used to gate the review banner. */
export const LEGAL_HAS_PLACEHOLDERS =
  LEGAL.CONTACT_EMAIL.includes('example.invalid') ||
  LEGAL.SITE_URL.includes('cloudfront.net') ||
  LEGAL.SITE_URL.includes('netlify.app');
